/**
 * SettingsDialog — Modal-based settings with sidebar navigation.
 * Replaces the old route-based settings pages with a single dialog overlay.
 * Left panel: grouped nav items with icons.
 * Right panel: lazy-loaded settings content.
 */

import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Activity,
  Bot,
  Boxes,
  Building2,
  Globe,
  Info,
  KeyRound,
  Mail,
  Settings as SettingsIcon,
  Sparkles,
  User,
  Users,
  Zap,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { lazy, Suspense, useCallback, useRef, useState, useEffect } from 'react'
import type { ComponentType } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/ui/user-avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCurrentUser } from '@/store/slices'
import { useCurrentWorkspace, useWorkspaces, useWorkspaceActions } from '@/store/slices/workspace-slice'
import { Check, ChevronsUpDown } from 'lucide-react'
import { SectionNavProvider } from '@/components/settings/section-nav-context'

/** Navigation item definition */
interface NavItem {
  key: string
  title: string
  icon: ComponentType<{ className?: string }>
  description: string
  /** When set, the item navigates to this route (and closes the dialog) instead of switching section. */
  href?: string
}

/** Navigation group definition */
interface NavGroup {
  /**
   * Optional section label rendered above the group's items. Omitted for
   * single-item top-level groups (e.g. the lone Profile entry) where a
   * heading would be visual noise above one row.
   */
  label?: string
  items: NavItem[]
}

/** All navigation groups */
const NAVIGATION_GROUPS: NavGroup[] = [
  {
    label: 'Personal',
    items: [
      { key: 'account', title: 'Account', icon: User, description: 'Profile, credits, and account-level controls' },
      { key: 'general', title: 'General', icon: SettingsIcon, description: 'Language, theme, security, and active sessions' },
      { key: 'personalization', title: 'Personalization', icon: Sparkles, description: 'Tune how your AI employees respond to you' },
      { key: 'usage', title: 'Usage', icon: Activity, description: 'Your activity in this workspace' },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { key: 'workspace-settings', title: 'Workspace', icon: Building2, description: 'Identity, branding, and authentication' },
      { key: 'team-members', title: 'Members', icon: Users, description: 'Members, roles, invitations' },
      { key: 'ai-configuration', title: 'AI Models', icon: Bot, description: 'Connect providers and choose available models' },
      { key: 'super-agent', title: 'Super Agent', icon: Sparkles, description: 'Tune the workspace assistant — model, compute, and instructions' },
      { key: 'credits', title: 'Billing', icon: Zap, description: 'Workspace credits, plan, and transactions' },
    ],
  },
  {
    label: 'Features',
    items: [
      { key: 'agent-mail', title: 'Agent Email', icon: Mail, description: 'Email channel settings' },
      { key: 'plugins', title: 'Plugins', icon: Boxes, description: 'Skills, connected apps, and MCP servers' },
      { key: 'external-apis', title: 'API Connections', icon: Globe, description: 'Configure services your agents can call' },
    ],
  },
  {
    label: 'Developer',
    items: [
      { key: 'api-keys', title: 'API Keys', icon: KeyRound, description: 'Manage API keys and tokens' },
    ],
  },
  {
    items: [
      { key: 'about', title: 'About', icon: Info, description: 'Version and system details' },
    ],
  },
]

/** Lazy-loaded section components */
const SECTIONS: Record<string, React.LazyExoticComponent<ComponentType>> = {
  'account': lazy(() => import('@/pages/app/settings/account').then(m => ({ default: m.AccountPage }))),
  'general': lazy(() => import('@/pages/app/settings/general').then(m => ({ default: m.GeneralPage }))),
  'personalization': lazy(() => import('@/pages/app/settings/personalization').then(m => ({ default: m.PersonalizationPage }))),
  'usage': lazy(() => import('@/pages/app/settings/usage').then(m => ({ default: m.UsagePage }))),
  'preferences': lazy(() => import('@/pages/app/settings/preferences').then(m => ({ default: m.PreferencesPage }))),
  'workspace-settings': lazy(() => import('@/pages/app/settings/workspace-settings').then(m => ({ default: m.WorkspaceSettingsPage }))),
  'ai-configuration': lazy(() => import('@/pages/app/settings/ai-configuration').then(m => ({ default: m.AiConfigurationPage }))),
  'super-agent': lazy(() => import('@/pages/app/settings/super-agent').then(m => ({ default: m.SuperAgentSettingsPage }))),
  'plugins': lazy(() => import('@/pages/app/settings/plugins').then(m => ({ default: m.PluginsPage }))),
  'external-apis': lazy(() => import('@/pages/app/settings/external-apis').then(m => ({ default: m.ExternalApisPage }))),
  'team-members': lazy(() => import('@/pages/app/settings/team-members').then(m => ({ default: m.TeamMembersPage }))),
  'credits': lazy(() => import('@/pages/app/settings/credits')),
  'agent-mail': lazy(() => import('@/pages/app/settings/agent-mail').then(m => ({ default: m.AgentMailSettingsPage }))),
  'api-keys': lazy(() => import('@/pages/app/settings/api-keys').then(m => ({ default: m.ApiKeysPage }))),
  'about': lazy(() => import('@/pages/app/settings/about').then(m => ({ default: m.AboutPage }))),
}

/** Loading skeleton for section content */
function SectionSkeleton() {
  return (
    <div className="space-y-6 p-6">
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      <Skeleton className="h-px w-full" />
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-3/4" />
      </div>
    </div>
  )
}

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialSection?: string
}

export function SettingsDialog({
  open,
  onOpenChange,
  initialSection = 'account',
}: SettingsDialogProps) {
  const [activeSection, setActiveSection] = useState(initialSection)
  const contentRef = useRef<HTMLDivElement>(null)

  /** Reset to initial section when dialog opens */
  useEffect(() => {
    if (open) {
      setActiveSection(initialSection)
    }
  }, [open, initialSection])

  /** Scroll content to top when switching sections */
  const handleSectionChange = useCallback((key: string) => {
    setActiveSection(key)
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [])

  const ActiveComponent = SECTIONS[activeSection]
  const user = useCurrentUser()
  const navigate = useNavigate()
  const navGroups: NavGroup[] = NAVIGATION_GROUPS
  const workspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const { switchWorkspace } = useWorkspaceActions()

  const userInitials = user?.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[92vw] !max-w-6xl h-[85vh] p-0 gap-0 overflow-hidden flex flex-col lg:flex-row sm:!max-w-6xl"
        showCloseButton
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>

        {/* Left sidebar — stacks above content on < lg */}
        <div className="w-full lg:w-[288px] max-h-[40%] lg:max-h-none border-b lg:border-b-0 lg:border-r bg-muted/30 shrink-0 flex flex-col">
          {/* User & Workspace header */}
          <div className="px-3 pt-5 pb-3 mb-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-3 w-full px-2 py-2 rounded-lg hover:bg-muted transition-colors text-left cursor-pointer"
                >
                  <UserAvatar
                    image={user?.avatar}
                    name={user?.name || 'User'}
                    initials={userInitials}
                    className="h-9 w-9 shrink-0"
                    fallbackClassName="bg-primary text-primary-foreground text-xs font-medium"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{workspace?.name || 'Workspace'}</p>
                  </div>
                  <ChevronsUpDown className="size-4 text-muted-foreground shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[228px]">
                {workspaces.map((ws) => (
                  <DropdownMenuItem
                    key={ws.id}
                    onClick={() => switchWorkspace(ws.id)}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate">{ws.name}</span>
                    {ws.id === workspace?.id && <Check className="size-4 shrink-0 text-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <ScrollArea className="flex-1">
            <nav className="px-3 pb-4 space-y-4">
              {navGroups.map((group, index) => (
                <div key={group.label ?? `unlabeled-${index}`}>
                  {group.label && (
                    <p className="type-label px-2 mb-1 text-muted-foreground text-xs font-medium uppercase tracking-wider">
                      {group.label}
                    </p>
                  )}
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const isActive = activeSection === item.key
                      const Icon = item.icon
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => {
                            if (item.href) {
                              navigate(item.href)
                              onOpenChange(false)
                            } else {
                              handleSectionChange(item.key)
                            }
                          }}
                          className={cn(
                            'flex items-center gap-2.5 w-full px-2 py-1.5 rounded-md text-left transition-colors text-sm cursor-pointer',
                            isActive
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                          )}
                        >
                          <Icon className={cn('size-4 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground')} />
                          <span className="truncate">{item.title}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </ScrollArea>
        </div>

        {/* Right content — centered column with breathing room on wide screens */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          <div className="px-4 py-4 lg:px-12 lg:py-8">
            <div className="mx-auto w-full max-w-2xl">
              <SectionNavProvider value={handleSectionChange}>
                <Suspense fallback={<SectionSkeleton />}>
                  {ActiveComponent && <ActiveComponent key={activeSection} />}
                </Suspense>
              </SectionNavProvider>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
